import React, { createRef, memo, useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";
import { VideoPlayerWrapper, VideoPlayerContainer } from "orion-components/CBComponents";
import { Button, Card, CardActions, CardContent, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { openDialog, closeDialog } from "orion-components/AppState/Dialog/actions";
import EmptySlot from "./EmptySlot";
import { v4 as uuidv4 } from "uuid";
import { setCameraPriority } from "orion-components/Dock/Actions/index.js";
import { selectCameraEntity } from "../../GlobalData/CameraClusters/selectors";
import { Cancel } from "@mui/icons-material";
import {
	DEFAULT_RECORDED_DURATION,
	DEFAULT_RECORDED_TIME_UNIT
} from "../../CBComponents/NewVideoPlayer/components/Recorded/Utils/videoDateRangeSelector";

const propTypes = {
	index: PropTypes.string,
	id: PropTypes.object,
	currentCol: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	currentRow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	columns: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	selectedClusterCameras: PropTypes.array,
	handleRemove: PropTypes.func,
	canAddCameras: PropTypes.bool,
	AddCameraToClusterDialog: PropTypes.func,
	clusterProps: PropTypes.object,
	isOnAlertProfile: PropTypes.bool,
	alertDateTime: PropTypes.string,
	isOnProfile: PropTypes.bool,
	canRemoveCameraFromSlot: PropTypes.bool
};

const defaultProps = {
	index: null,
	gridHeight: null,
	handleRemove: () => {
		return;
	},
	canAddCameras: false,
	AddCameraToClusterDialog: () => {
		return <></>;
	},
	clusterProps: {},
	isOnAlertProfile: false,
	isOnProfile: false,
	canRemoveCameraFromSlot: false
};

const getStyles = () => ({
	container: { backgroundColor: "#1F1F21", height: "100%", width: "100%" },
	cardActions: { padding: 0, justifyContent: "space-between" },
	cardContent: {
		padding: 0,
		height: "100%"
	}
});

const normalizeTourCameraEntry = cameraEntry => ({
	cameraId: cameraEntry?.cameraId ?? null,
	preset: cameraEntry?.preset ?? null
});

const CameraSlot = ({
	index,
	currentCol,
	currentRow,
	selectedClusterCameras,
	handleRemove,
	canAddCameras,
	rows,
	columns,
	AddCameraToClusterDialog,
	clusterProps,
	isOnAlertProfile,
	alertDateTime,
	isOnProfile,
	canRemoveCameraFromSlot
}) => {
	const profile = useSelector(state => state.session.user.profile);
	const dock = useSelector(state => state.appState.dock);
	const Dialog = useSelector(state => state.appState.dialog);
	const videoTours = useSelector(state => state.globalData.videoTours);

	const instanceIdRef = useRef(uuidv4());

	const [styles, setStyles] = useState({});
	const [camera, setCamera] = useState(null);

	useEffect(() => {
		setStyles(getStyles());
	}, []);
	const { cameraPriority, dockedCameras } = dock.cameraDock;

	const cameraId = selectedClusterCameras?.[currentRow]?.[currentCol]?.cameraId ?? "";
	const tourId = selectedClusterCameras?.[currentRow]?.[currentCol]?.tourId ?? "";
	const cameraSlotProps = selectedClusterCameras?.[currentRow]?.[currentCol];
	const tour = tourId && videoTours?.[tourId];
	const normalizedTourCameras = (tour?.cameras || []).map(normalizeTourCameraEntry).filter(item => item.cameraId);
	const cameraIds = cameraId ? [cameraId] : normalizedTourCameras.map(item => item.cameraId);
	const dialogKey = `camera-wall-${(cameraId || tourId) ?? ""}`;
	const docked = cameraId && cameraPriority.dockOpen && dockedCameras.includes(cameraId);

	// Create selector only when inputs change
	const selectCamera = useMemo(() => selectCameraEntity(cameraIds, isOnAlertProfile), [cameraIds, isOnAlertProfile]);

	// Get store data
	const cameraFromStore = useSelector(selectCamera);

	useEffect(() => {
		const nextCameraState =
			Array.isArray(cameraFromStore) && normalizedTourCameras.length > 0
				? cameraFromStore.map((cameraEntity, index) => ({
						...cameraEntity,
						preset: normalizedTourCameras[index]?.preset ?? null
					}))
				: cameraFromStore;

		if (!isEqual(camera, nextCameraState)) setCamera(nextCameraState);
	}, [camera, cameraFromStore, normalizedTourCameras]);

	const modal = Dialog?.openDialog === dialogKey || cameraPriority?.modalOpen;

	const dialog = Dialog.openDialog;

	const dir = useSelector(state => getDir(state));
	const dispatch = useDispatch();
	const cardContainerRef = createRef();

	const handleOpenDialog = dialogKey => {
		dispatch(openDialog(dialogKey));
	};

	const handleCloseDialog = dialogKey => {
		dispatch(closeDialog(dialogKey));
	};

	const handleSetCameraPriority = (dockOpen, modalOpen) => {
		dispatch(setCameraPriority(dockOpen, modalOpen));
	};

	if (camera !== "Not available" && camera?.length > 0) {
		const defaultToRecorded = Boolean(!tour && cameraSlotProps?.cameraMode === "recorded");
		const initialRecordedDuration =
			!tour && cameraSlotProps?.duration !== undefined
				? cameraSlotProps.duration
				: isOnAlertProfile
					? DEFAULT_RECORDED_DURATION
					: undefined;
		const initialRecordedTimeUnit =
			!tour && cameraSlotProps?.unitTime !== undefined
				? cameraSlotProps.unitTime
				: isOnAlertProfile
					? DEFAULT_RECORDED_TIME_UNIT
					: undefined;
		const canControl =
			!tour &&
			profile.integrations &&
			profile.integrations.find(int => int.intId === camera[0].feedId) &&
			profile.integrations.find(int => int.intId === camera[0].feedId).permissions &&
			profile.integrations.find(int => int.intId === camera[0].feedId).permissions.includes("control");

		return (
			<Card style={styles.container} ref={cardContainerRef}>
				{!tour && !camera[0].player?.newPlayer && (
					<CardActions style={styles.cardActions}>
						<Button
							color="primary"
							onClick={() => dispatch(openDialog(dialogKey))}
							style={{ textTransform: "none" }} data-testid="orion-components-shared-components-camera-clusters-camera-slot-button"
						>
							{camera[0]?.entityData?.properties?.name ?? ""}
						</Button>
						{canRemoveCameraFromSlot && (
							<IconButton onClick={() => handleRemove(currentRow, currentCol)} style={{ padding: 8 }} data-testid="orion-components-shared-components-camera-clusters-camera-slot-icon-button">
								<Cancel />
							</IconButton>
						)}
					</CardActions>
				)}
				<CardContent style={styles.cardContent}>
					{tour || camera[0].player?.newPlayer ? (
						<VideoPlayerContainer
							key={instanceIdRef.current}
							instanceId={instanceIdRef.current}
							camera={camera}
							namePrefix={tour?.name ?? null}
							dwellTime={tour?.dwellTime ?? null}
							canControl={canControl}
							closeDialog={handleCloseDialog}
							dialog={dialog}
							dialogKey={dialogKey}
							docked={docked}
							modal={modal}
							openDialog={handleOpenDialog}
							fillAvailable={true}
							dir={dir}
							expanded={false}
							isVideoWall={true}
							removeFromWall={handleRemove}
							setCameraPriority={handleSetCameraPriority}
							clusterProps={clusterProps}
							isOnAlertProfile={isOnAlertProfile}
							isOnProfile={isOnProfile}
							alertDateTime={alertDateTime}
							defaultToRecorded={defaultToRecorded}
							initialRecordedDuration={initialRecordedDuration}
							initialRecordedTimeUnit={initialRecordedTimeUnit}
							disableVideoModeToggle={!!tour}
						/>
					) : (
						<VideoPlayerWrapper
							camera={camera[0]} // Does not support tours yet
							canControl={canControl}
							closeDialog={handleCloseDialog}
							dialog={dialog}
							dialogKey={dialogKey}
							docked={docked}
							modal={modal}
							openDialog={handleOpenDialog}
							fillAvailable={true}
							dir={dir}
							expanded={false}
							removeFromWall={handleRemove}
							setCameraPriority={handleSetCameraPriority}
						/>
					)}
				</CardContent>
			</Card>
		);
	} else {
		return (
			<EmptySlot
				key={index}
				camera={camera}
				canAddCameras={canAddCameras}
				rows={rows}
				columns={columns}
				currentCol={currentCol}
				currentRow={currentRow}
				AddCameraToClusterDialog={AddCameraToClusterDialog}
			/>
		);
	}
};

CameraSlot.propTypes = propTypes;
CameraSlot.defaultProps = defaultProps;

export default memo(CameraSlot);
